/**
 * Runtime shading upgrades, injected into MeshStandard/MeshPhysicalMaterial through
 * onBeforeCompile. Everything here is opt-in per material.
 * owner: materials
 *
 *   triplanar  world-space projection, no UV stretch on terrain / large surfaces
 *   pom        parallax occlusion mapping from the height packed in normalMap.a
 *   detail     shared high-frequency detail normal + micro-roughness
 *   hex        stochastic hex-grid tile break-up (distance gated)
 *   macro      low-frequency world-space albedo/roughness variation, plus a SECOND
 *              roughness-only field on its own scale (roughField) so gloss is not
 *              tied to value — see the SURF_MACRO block
 *   edgeWear   curvature-driven wear: convex geometry and proud, open relief blend
 *              toward a worn albedo / roughness / metalness. FINDINGS F20 #2.
 *   specAA     roughness clamp derived from shading-normal variance
 *
 * All maps of a material share UV channel 0 and the same repeat, so vMapUv is used
 * for every sample.
 */

import * as THREE from 'three';

const PARS = /* glsl */`
varying vec3 vSurfWPos;
varying vec3 vSurfWNrm;

uniform vec4  uDetailP;   // x tiling, y normal strength, z roughness strength, w height datum
uniform vec4  uMacroP;    // x world scale, y albedo var, z roughness var, w hue var
uniform vec4  uMacroP2;   // x roughness-field scale (1/m), y amplitude, z albedo coupling, w spare
uniform vec4  uPomP;      // x height scale, y max layers, z fade start, w fade end
uniform vec4  uTileP;     // x triplanar world scale, y triplanar sharpness, z hex near, w hex far
uniform vec4  uUvXform;   // xy repeat, zw offset — kept out of texture.matrix so several
                          // materials can share one generated map set at different tilings
uniform float uAoIntensity;
uniform vec4  uTransP;    // x strength, y forward-scatter power, z wrap, w tint blend
uniform vec3  uTransTint;
uniform vec4  uOctP;      // x scale of the decorrelated octave, y albedo gain, z roughness gain, w rotation
uniform vec4  uDetail2;   // x albedo modulation from the detail height, yzw spare
uniform vec4  uFacadeP;   // x plinth top (m), y storey height (m), z sill height in storey, w streak strength
uniform vec4  uFacadeJ;   // x value jitter, y hue jitter, z roughness jitter, w patch size (1/m)
uniform vec4  uMiscP;     // x 1/anisoRatio, y max minor-axis widening, z hex restore clamp, w metal gain
uniform vec4  uJitterP;   // x cells per metre, y uv offset amount, zw spare
uniform vec4  uSpecP;     // x IBL weight, y specular tint amount, z spare, w spare
uniform vec4  uWetP;      // x world scale, y strength, z threshold, w wet roughness floor
uniform vec4  uSssP;      // x strength, yzw per-channel diffusion wrap (R deepest)
uniform vec4  uSssP2;     // x shadow floor, y curvature gain, z spare, w spare
uniform vec3  uSssTint;   // transmitted colour (haemoglobin: red survives, green/blue do not)
uniform vec3  uSssAmb;    // indirect-diffuse tint, so sky-lit skin reads cooler than sunlit skin
uniform vec4  uEdgeP;     // x strength, y curvature onset (1/m), z worn roughness, w worn metalness
uniform vec4  uEdgeP2;    // x relief weight, y height datum, z break-up scale (1/m), w geo fade (m)
uniform vec3  uEdgeCol;   // albedo the surface goes to where it has been knocked

vec2 gUV;
vec4 gALB;
vec4 gORM;
vec3 gNTS;
vec3 gWNRM;
mat3 gTBN;
float gHGT;               // the material's own height field, 0..1 (normalMap.a)
float gSLP;               // 1 - N.z of the BASE tangent normal: relief slope, pre-detail

/* ---- cheap value noise (runtime only, not CPU-matched) ------------------ */
float sHash21(vec2 p){
  vec3 p3 = fract(p.xyx * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 sHash22(vec2 p){
  vec3 p3 = fract(p.xyx * vec3(0.1031, 0.1030, 0.0973));
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.xx + p3.yz) * p3.zy);
}
float sVNoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(sHash21(i), sHash21(i + vec2(1, 0)), f.x),
             mix(sHash21(i + vec2(0, 1)), sHash21(i + vec2(1, 1)), f.x), f.y);
}
float sFbm(vec2 p){
  return sVNoise(p) * 0.55 + sVNoise(p * 2.13 + 5.1) * 0.28 + sVNoise(p * 4.7 + 11.3) * 0.17;
}

/* ---- tangent frame (self-contained, no dependency on three internals) --- */
mat3 surfTBN(vec3 N, vec2 uv){
  vec3 p = -vViewPosition;                 // view-space fragment position
  vec3 dp1 = dFdx(p), dp2 = dFdy(p);
  vec2 du1 = dFdx(uv), du2 = dFdy(uv);
  vec3 dp2perp = cross(dp2, N);
  vec3 dp1perp = cross(N, dp1);
  vec3 T = dp2perp * du1.x + dp1perp * du2.x;
  vec3 B = dp2perp * du1.y + dp1perp * du2.y;
  float k = inversesqrt(max(dot(T, T), dot(B, B)));
  return mat3(T * k, B * k, N);
}

vec3 blendNormalUDN(vec3 base, vec3 det, float k){
  det = mix(vec3(0.0, 0.0, 1.0), det, k);
  return normalize(vec3(base.xy + det.xy, base.z * det.z));
}

/* ---- stochastic hex tiling (Heitz/Neyret style, 3 taps) ----------------
 *
 * Each of the three taps gets a random OFFSET *and* a random rotation/scale about
 * the tile centre. Offset alone leaves every tap on the same axis-aligned lattice,
 * so any structure the generator put on a grid — form-tie holes, aggregate cells,
 * the tile boundary itself — stays coherent across the whole surface and the
 * repeat is still findable. Rotating each tap decorrelates the lattice as well as
 * the phase, which is what actually kills the "35 px dark-dot grid".
 */
struct HexTap { vec2 o; mat2 m; };

void hexTile(vec2 uv, float rot, out HexTap t1, out HexTap t2, out HexTap t3, out vec3 w){
  const mat2 toSkew = mat2(1.0, 0.0, -0.57735027, 1.15470054);
  vec2 sk = toSkew * uv * 3.4641016;
  vec2 base = floor(sk);
  vec2 fr = fract(sk);
  vec3 bary = vec3(fr.x, fr.y, 1.0 - fr.x - fr.y);
  vec2 v1, v2, v3;
  if (bary.z > 0.0){
    w  = vec3(bary.z, bary.y, bary.x);
    v1 = base; v2 = base + vec2(0.0, 1.0); v3 = base + vec2(1.0, 0.0);
  } else {
    w  = vec3(-bary.z, 1.0 - bary.y, 1.0 - bary.x);
    v1 = base + vec2(1.0, 1.0); v2 = base + vec2(1.0, 0.0); v3 = base + vec2(0.0, 1.0);
  }
  t1.o = sHash22(v1) * 0.9;
  t2.o = sHash22(v2) * 0.9;
  t3.o = sHash22(v3) * 0.9;
  // per-cell rotation, quantised to 90 degree steps plus a small random tilt so
  // the texel footprint stays close to isotropic and the gradients stay valid
  float a1 = (floor(sHash21(v1 + 7.13) * 4.0) * 1.5707963 + (sHash21(v1 + 3.1) - 0.5) * 0.55) * rot;
  float a2 = (floor(sHash21(v2 + 7.13) * 4.0) * 1.5707963 + (sHash21(v2 + 3.1) - 0.5) * 0.55) * rot;
  float a3 = (floor(sHash21(v3 + 7.13) * 4.0) * 1.5707963 + (sHash21(v3 + 3.1) - 0.5) * 0.55) * rot;
  t1.m = mat2(cos(a1), -sin(a1), sin(a1), cos(a1));
  t2.m = mat2(cos(a2), -sin(a2), sin(a2), cos(a2));
  t3.m = mat2(cos(a3), -sin(a3), sin(a3), cos(a3));
  // sharpen so the blend bands stay narrow, then renormalise
  w = w * w * w * w;
  w /= (w.x + w.y + w.z);
}

/* Saturating soft clamp: 1 at g=1, asymptotic to cap, C1-continuous everywhere.
 *
 * A hard min(g, cap) has a DISCONTINUOUS DERIVATIVE on the locus g == cap, and on the
 * triangular hex lattice that locus is a set of straight lines. A kink in the contrast
 * gain along a straight line is a straight line you can see — and, because SURF_SPECAA
 * derives its roughness kernel from dFdx(normal), a kink is also a local roughness
 * spike, which prints as a DARK HAIRLINE. That is exactly the "regular diagonal lattice
 * of dark hairlines at ~120 px spacing" the critique found on mat_closeup_concrete:
 * the tile-breaking machinery was drawing its own findable tile. */
float softCap(float g, float cap){
  float t = (g - 1.0) / max(cap - 1.0, 1e-3);
  return 1.0 + (cap - 1.0) * (1.0 - exp(-max(t, 0.0)));
}

vec4 hexFetch(sampler2D t, vec2 uv, vec2 dx, vec2 dy, HexTap t1, HexTap t2, HexTap t3, vec3 w, float restore){
  vec4 a = textureGrad(t, t1.m * uv + t1.o, t1.m * dx, t1.m * dy);
  vec4 b = textureGrad(t, t2.m * uv + t2.o, t2.m * dx, t2.m * dy);
  vec4 c = textureGrad(t, t3.m * uv + t3.o, t3.m * dx, t3.m * dy);
  vec4 s = a * w.x + b * w.y + c * w.z;
  if (restore > 0.5){
    /* Variance-preserving recombination against the texture mean (top mip).
     * inversesqrt(dot(w,w)) peaks at sqrt(3) exactly on the lines where the three taps
     * carry equal weight — i.e. along every cell edge of the triangular lattice — so the
     * gain HAS to be limited, but it has to be limited SMOOTHLY (see softCap). */
    vec4 m = textureLod(t, uv, 14.0);
    float gain = softCap(inversesqrt(max(dot(w, w), 1e-4)), uMiscP.z);
    s = (s - m) * gain + m;
  }
  return s;
}
`;

const SAMPLE_FN = /* glsl */`
/* Tangent normals have to be counter-rotated per tap: the map stores the height
   gradient in the *rotated* UV frame, so blending the raw xy would leave every
   hex cell lit from a different direction. grad_base = transpose(R) * grad_rot. */
vec4 hexFetchNormal(vec2 uv, vec2 dx, vec2 dy, HexTap t1, HexTap t2, HexTap t3, vec3 w){
  vec4 a = textureGrad(normalMap, t1.m * uv + t1.o, t1.m * dx, t1.m * dy);
  vec4 b = textureGrad(normalMap, t2.m * uv + t2.o, t2.m * dx, t2.m * dy);
  vec4 c = textureGrad(normalMap, t3.m * uv + t3.o, t3.m * dx, t3.m * dy);
  a.xy = transpose(t1.m) * (a.xy * 2.0 - 1.0) * 0.5 + 0.5;
  b.xy = transpose(t2.m) * (b.xy * 2.0 - 1.0) * 0.5 + 0.5;
  c.xy = transpose(t3.m) * (c.xy * 2.0 - 1.0) * 0.5 + 0.5;
  vec4 s = a * w.x + b * w.y + c * w.z;
  /* THE RELIEF AMPLITUDE HAS TO BE RESTORED TOO, and it was not.
   *
   * The tangent xy is a height GRADIENT with mean zero. Averaging three decorrelated
   * gradient fields shrinks the result by ~1/sqrt(3) at the cell corners and 1/sqrt(2)
   * along the cell edges — so the surface goes FLAT in a triangular lattice of bands
   * while staying in relief at the cell centres. A band of flattened normal is a band of
   * different shading, and it is drawn along exactly the same lines as the albedo
   * restore. Restoring the gradient variance against its own zero mean puts the relief
   * back and the lattice loses the thing that made it visible.
   *
   * The gain here runs to the full sqrt(3): unlike the albedo there is no risk of
   * over-contrasting a mean, because the mean of a gradient field is zero by
   * construction. Clamped anyway so a degenerate weight cannot explode the normal. */
  float gain = softCap(inversesqrt(max(dot(w, w), 1e-4)), 1.75);
  s.xy = (s.xy - 0.5) * gain + 0.5;
  return s;
}

/* Bound the anisotropy of the sample footprint.
 *
 * A 40 m wall seen down its own length has a texel footprint ratio in the
 * hundreds. The hardware stops at 16, so past that the minor axis is
 * under-filtered and every high-contrast line in the map — mortar course, board
 * seam, plank edge — is drawn as a long clean streak that swims when the camera
 * moves. Widening the minor axis until the ratio is 8 trades a little sharpness
 * at grazing for a surface that resolves instead of shearing, which is the same
 * bargain a mip bias makes but only where it is actually needed.
 */
/* Ratio bounded at the hardware's own 16 (uMiscP.x) AND the widening itself capped
 * (uMiscP.y). The old 1/8 limit with no cap widened the minor axis up to ~25x on a
 * grazing ground plane; the minor axis there is the ACROSS-view direction, so the
 * texture was blurred across the view and left coherent along it — a fan of straight
 * streaks radiating from the vanishing point, which is what the market square deck
 * showed. Capping the widening keeps the shear cure without the smear. */
void surfAniso(inout vec2 dx, inout vec2 dy){
  float lx = max(length(dx), 1e-9);
  float ly = max(length(dy), 1e-9);
  float lim = max(lx, ly) * uMiscP.x;
  /* The allowance RAMPS with how far over the hardware limit the footprint already is.
   *
   * A flat cap of 3x was fitted on a ground plane whose ratio is ~24-48, where 3x is
   * exactly enough and any more visibly smears. It is not enough at 34 cm from a 26 m
   * panel, where the ratio runs into the hundreds: 16 anisotropic taps spread over a
   * footprint that long under-sample ALONG the major axis, and under-sampling along the
   * view axis is drawn as a fan of streaks — "the left third of mat_closeup_concrete
   * dissolves into horizontal streaks". Letting the allowance grow only once the ratio
   * is well past the limit keeps the measured mid-range behaviour untouched and pays
   * for the grazing case with blur, which is the correct currency. */
  float over = max(lx, ly) / min(lx, ly) * uMiscP.x;
  float allow = mix(uMiscP.y, uMiscP.y * 1.8, clamp((over - 3.0) / 6.0, 0.0, 1.0));
  if (lx < lim) dx *= min(lim / lx, allow);
  if (ly < lim) dy *= min(lim / ly, allow);
}

/* one full material fetch (albedo + orm + tangent normal) at uv */
void surfFetch(vec2 uv, vec2 dx, vec2 dy, float hexAmt, out vec4 alb, out vec4 orm, out vec3 nts){
  surfAniso(dx, dy);
  vec4 a = textureGrad(map, uv, dx, dy);
  vec4 o = textureGrad(roughnessMap, uv, dx, dy);
  vec4 n = textureGrad(normalMap, uv, dx, dy);
  #ifdef SURF_HEX
  /* Cross-fade rather than switch. Taking the hex path the instant hexAmt clears
     a threshold draws a hard arc across the frame wherever the distance band is
     crossed — on a wall raked away from the camera that arc lands straight down
     the middle of the shot. The band is narrow, so paying for both paths inside
     it is cheap and it is the only way the transition is invisible. */
  if (hexAmt > 0.004){
    HexTap t1, t2, t3; vec3 w;
    hexTile(uv * 0.37, hexAmt, t1, t2, t3, w);
    float k = smoothstep(0.0, 1.0, hexAmt);
    a = mix(a, hexFetch(map,          uv, dx, dy, t1, t2, t3, w, 1.0), k);
    o = mix(o, hexFetch(roughnessMap, uv, dx, dy, t1, t2, t3, w, 0.0), k);
    n = mix(n, hexFetchNormal(uv, dx, dy, t1, t2, t3, w), k);
  }
  #endif
  alb = a;
  orm = o;
  gHGT = n.a;
  nts = normalize(n.xyz * 2.0 - 1.0 + vec3(0.0, 0.0, 1e-4));
  gSLP = 1.0 - clamp(nts.z, 0.0, 1.0);
}

/* Second, decorrelated octave of the same map set.
 *
 * One texture repeat is one spatial frequency, and a surface built from a single
 * frequency is exactly what makes a tile findable — the eye locks onto the beat.
 * Sampling the albedo again at an incommensurate scale and angle adds a second
 * octave of value/roughness variation that shares no period with the first, for
 * one extra tap and no extra texture image unit.
 */
void surfOctave(vec2 uv, vec2 dx, vec2 dy, inout vec4 alb, inout vec4 orm){
  float ca = cos(uOctP.w), sa = sin(uOctP.w);
  mat2 R = mat2(ca, -sa, sa, ca) * uOctP.x;
  vec2 uv2 = R * uv + vec2(0.317, 0.719);
  /* THIS TAP MUST NOT BE SAMPLED SHARPER THAN THE BASE, AND MUST BE ANISO-BOUNDED.
   *
   * It was neither, and that is what put a fan of streaks radiating from the vanishing
   * point across the whole market square deck and the tiling-test corridor floor
   * (proved by ablation: disabling only this layer removes them completely and nothing
   * else does). Two compounding mistakes:
   *   - R scales the gradients by uOctP.x = 0.28, so on a minified ground plane this
   *     tap sat ~1.8 mips below the base tap. The base was safely in the mips; this one
   *     was not, so ALL the visible high-frequency energy at distance came from here.
   *   - the gradients never went through surfAniso, so this tap alone carried the raw
   *     footprint ratio — in the hundreds at grazing incidence — and the hardware's 16
   *     taps under-filter it along the view axis. Under-filtering along the view axis
   *     is, drawn out, a radial streak.
   * Restoring the base footprint length keeps what this layer is FOR (a decorrelated
   * low-frequency octave that shares no period with the first) and discards only the
   * detail it was never entitled to resolve.
   */
  vec2 dx2 = R * dx, dy2 = R * dy;
  float baseLen = max(length(dx), length(dy));
  float octLen = max(max(length(dx2), length(dy2)), 1e-9);
  float k = max(baseLen / octLen, 1.0);
  dx2 *= k; dy2 *= k;
  surfAniso(dx2, dy2);
  vec4 o = textureGrad(map, uv2, dx2, dy2);
  vec4 mean = textureLod(map, uv2, 14.0);
  float v = dot(o.rgb - mean.rgb, vec3(0.3333)) / max(dot(mean.rgb, vec3(0.3333)), 0.02);
  alb.rgb *= 1.0 + clamp(v, -1.5, 1.5) * uOctP.y;
  orm.g = clamp(orm.g + clamp(v, -1.5, 1.5) * uOctP.z, 0.03, 1.0);
}
`;

const POM_FN = /* glsl */`
vec2 surfPOM(vec2 uv, vec3 vts, vec2 dx, vec2 dy, float fade){
  // more layers as the view flattens out — that is where stepping shows
  float nl = mix(uPomP.y, 10.0, sqrt(clamp(abs(vts.z), 0.0, 1.0)));
  float layerD = 1.0 / nl;
  /* Clamping |vts.z| at 0.35 let the parallax offset reach ~3x the height scale
     on a wall raked away from the camera. Past that the march samples along a
     nearly tangential line and the relief smears into long horizontal streaks —
     which is what a 40 m brick corridor viewed down its length showed. 0.6 caps
     the offset at ~1.7x, where the stepping is still hidden by the refinement. */
  vec2 P = vts.xy / max(abs(vts.z), 0.60) * uPomP.x * fade;
  /* additionally taper the depth as the view flattens: at true grazing the eye
     cannot resolve the depth anyway, and the artefact is all that survives */
  /* 0.35 -> 0.12 at the flat end. At |vts.z| under ~0.1 the eye cannot resolve the
   * relief at all, so every bit of parallax offset that survives there is pure
   * artefact — a long smear along the view axis, which is the other half of what the
   * left third of the grazing closeup was showing. */
  P *= mix(0.12, 1.0, smoothstep(0.06, 0.55, abs(vts.z)));
  vec2 dUV = P * layerD;
  float curD = 0.0;
  vec2 cur = uv;
  float h = 1.0 - textureGrad(normalMap, cur, dx, dy).a;
  for (int i = 0; i < 64; i++){
    if (float(i) >= nl || curD >= h) break;
    cur -= dUV;
    h = 1.0 - textureGrad(normalMap, cur, dx, dy).a;
    curD += layerD;
  }
  // binary refine: linear interpolation staircases badly on the hard height cliffs
  // that plank gaps and brick joints produce
  vec2 pIn = cur, pOut = cur + dUV;
  float dIn = curD, dOut = curD - layerD;
  for (int j = 0; j < 5; j++){
    vec2 pm = (pIn + pOut) * 0.5;
    float dm = (dIn + dOut) * 0.5;
    float hm = 1.0 - textureGrad(normalMap, pm, dx, dy).a;
    if (dm >= hm) { pIn = pm; dIn = dm; } else { pOut = pm; dOut = dm; }
  }
  return (pIn + pOut) * 0.5;
}
`;

const MAP_FRAGMENT = /* glsl */`
{
  vec3 Ngeo = normalize(vNormal);
  #ifdef DOUBLE_SIDED
    Ngeo *= gl_FrontFacing ? 1.0 : -1.0;
  #endif

  float viewDist = length(vViewPosition);
  float hexAmt = 1.0;
  #ifdef SURF_HEX
    // Distance band. Very close you only see one tile, so the 3x cost buys nothing and
    // that is exactly where the pixel count is highest; very far the mip chain already
    // hides the repeat. Pay for the break-up only in the mid ground, where a repeating
    // texture is actually readable.
    hexAmt = smoothstep(uTileP.z * 0.35, uTileP.z, viewDist)
           * (1.0 - smoothstep(uTileP.w, uTileP.w * 1.7, viewDist));
  #endif

  #ifdef SURF_TRIPLANAR
    vec3 wn = normalize(vSurfWNrm);
    vec3 bw = pow(abs(wn), vec3(uTileP.y));
    bw /= max(bw.x + bw.y + bw.z, 1e-4);
    float ts = uTileP.x;
    vec2 uvX = vec2(-vSurfWPos.z, vSurfWPos.y) * ts;
    vec2 uvY = vec2( vSurfWPos.x, -vSurfWPos.z) * ts;
    vec2 uvZ = vec2( vSurfWPos.x,  vSurfWPos.y) * ts;

    vec4 aX, aY, aZ, oX, oY, oZ; vec3 nX, nY, nZ;
    surfFetch(uvX, dFdx(uvX), dFdy(uvX), hexAmt, aX, oX, nX); float hX = gHGT, sX = gSLP;
    surfFetch(uvY, dFdx(uvY), dFdy(uvY), hexAmt, aY, oY, nY); float hY = gHGT, sY = gSLP;
    surfFetch(uvZ, dFdx(uvZ), dFdy(uvZ), hexAmt, aZ, oZ, nZ); float hZ = gHGT, sZ = gSLP;
    gHGT = hX * bw.x + hY * bw.y + hZ * bw.z;
    gSLP = sX * bw.x + sY * bw.y + sZ * bw.z;

    gALB = aX * bw.x + aY * bw.y + aZ * bw.z;
    gORM = oX * bw.x + oY * bw.y + oZ * bw.z;
    gUV  = uvZ;

    // whiteout blend into world space
    vec3 wX = vec3(nX.xy + wn.zy, abs(nX.z) * wn.x);
    vec3 wY = vec3(nY.xy + wn.xz, abs(nY.z) * wn.y);
    vec3 wZ = vec3(nZ.xy + wn.xy, abs(nZ.z) * wn.z);
    gWNRM = normalize(wX.zyx * bw.x + wY.xzy * bw.y + wZ.xyz * bw.z);
    gTBN = surfTBN(Ngeo, uvZ);
    gNTS = vec3(0.0, 0.0, 1.0);

    #ifdef SURF_DETAIL
    {
      // Self-detail: the material's own normal map re-sampled at a high tiling rate.
      // Costs no extra texture image unit (the fragment stage only has 16 and the
      // shadow maps eat most of them), and the micro-structure is automatically the
      // right kind for the surface — concrete grain on concrete, fibre on timber.
      float dt = uDetailP.x;
      float dca = cos(uDetail2.y), dsa = sin(uDetail2.y);
      mat2 dR = mat2(dca, -dsa * uDetail2.z, dsa, dca * uDetail2.z);
      vec4 dnX = texture(normalMap, dR * (uvX * dt) + vec2(0.371, 0.517));
      vec4 dnY = texture(normalMap, dR * (uvY * dt) + vec2(0.371, 0.517));
      vec4 dnZ = texture(normalMap, dR * (uvZ * dt) + vec2(0.371, 0.517));
      vec4 dn = dnX * bw.x + dnY * bw.y + dnZ * bw.z;
      vec3 dts = normalize(dn.xyz * 2.0 - 1.0 + vec3(0.0, 0.0, 1e-4));
      vec3 upv = abs(gWNRM.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
      vec3 Tw = normalize(cross(upv, gWNRM));
      vec3 Bw = cross(gWNRM, Tw);
      gWNRM = normalize(Tw * dts.x * uDetailP.y + Bw * dts.y * uDetailP.y + gWNRM * dts.z);
      gORM.g = clamp(gORM.g + (dn.a - uDetailP.w) * uDetailP.z, 0.02, 1.0);
      // the micro layer also darkens the pits — normal-only detail reads as
      // embossed plastic because the albedo stays perfectly uniform
      gALB.rgb *= 1.0 + (dn.a - uDetailP.w) * uDetail2.x;
    }
    #endif

    #ifdef SURF_OCTAVE
    {
      /* Drive the octave from the DOMINANT projection.
       *
       * This was hard-wired to uvZ = (world.x, world.y). On a ground plane the blend
       * weight is almost entirely on Y and world.y is constant, so the octave became a
       * function of world X alone — a modulation that varies across the street and not
       * at all along it, i.e. dead-straight lines running down the view axis. In
       * perspective that is a fan of streaks converging on the vanishing point, and it
       * is exactly what covered the market-square deck and the tiling-test floor.
       * Ablation: disabling only this layer removes them completely (mat_tiling_test
       * A_base vs B_noOctave); no other layer does.
       *
       * Derivatives are taken outside the selection so they stay quad-uniform. */
      vec2 dxX = dFdx(uvX), dyX = dFdy(uvX);
      vec2 dxY = dFdx(uvY), dyY = dFdy(uvY);
      vec2 dxZ = dFdx(uvZ), dyZ = dFdy(uvZ);
      float wy = step(bw.x, bw.y) * step(bw.z, bw.y);            // 1 when Y dominates
      float wx = (1.0 - wy) * step(bw.z, bw.x);                  // else 1 when X does
      float wz = 1.0 - wy - wx;
      vec2 uvO = uvX * wx + uvY * wy + uvZ * wz;
      vec2 dxO = dxX * wx + dxY * wy + dxZ * wz;
      vec2 dyO = dyX * wx + dyY * wy + dyZ * wz;
      surfOctave(uvO, dxO, dyO, gALB, gORM);
    }
    #endif
  #else
    vec2 uv0 = vMapUv * uUvXform.xy + uUvXform.zw;
    vec2 dx = dFdx(uv0), dy = dFdy(uv0);
    gTBN = surfTBN(Ngeo, uv0);

    /* ---- per-cell world-space UV offset -------------------------------------
     * Window glass is authored one pane at a time with the same 0..1 UV span on
     * every pane, so every pane got the identical grime mottle and the eye reads
     * the wall as a decal sheet. A constant offset per world-space cell moves each
     * pane to a different part of the same map: derivatives are unchanged (it is a
     * constant), it costs no sampler, and the cell boundary lands on the mullion. */
    #ifdef SURF_UVJITTER
    {
      /* A CONTINUOUS warp, not a per-cell constant. The per-cell version worked — the
       * panes stopped repeating — but any pane wider than a cell got a hard seam across
       * its middle where the cell index changed. An fbm warp at roughly the pane pitch
       * decorrelates neighbouring panes just as well and has no boundary anywhere. It
       * is low frequency, so the derivatives computed above stay valid. */
      vec3 wp = vSurfWPos * uJitterP.x;
      uv0 += (vec2(sFbm(wp.xz + wp.y * 0.71),
                   sFbm(wp.zx * 1.31 + wp.y * 0.53 + 17.3)) - 0.5) * uJitterP.y;
    }
    #endif

    #ifdef SURF_POM
    {
      /* The tangent frame from surfTBN() is deliberately NOT orthonormal — T and B
         carry the UV-to-world scale, which is what makes the normal map respond
         correctly to a stretched UV layout. Feeding that frame straight into the
         parallax view vector is a bug: T and B are scaled by k while N is unit
         length, so vts.z is on a different scale from vts.xy and the marching
         offset (vts.xy / vts.z) blows up as the surface turns away. That is what
         combed a plank wall into vertical streaks at 34 cm. POM needs the
         *directions*, so normalise them here and leave the shading frame alone. */
      vec3 Tn = normalize(gTBN[0]);
      vec3 Bn = normalize(gTBN[1]);
      vec3 vts = normalize(vec3(dot(vViewPosition, Tn), dot(vViewPosition, Bn), dot(vViewPosition, gTBN[2])));
      float fade = 1.0 - smoothstep(uPomP.z, uPomP.w, viewDist);
      if (fade > 0.01) uv0 = surfPOM(uv0, vts, dx, dy, fade);
    }
    #endif

    surfFetch(uv0, dx, dy, hexAmt, gALB, gORM, gNTS);
    gUV = uv0;

    #ifdef SURF_DETAIL
    {
      /* Self-detail: the material's own normal map re-sampled at a high tiling rate,
       * ROTATED. Without the rotation the re-tiled copy is axis-aligned with the base,
       * so any directional structure the generator authored — plank courses, butt
       * joints, brick beds, weave — comes back as a miniature copy of itself in
       * perfect register: a 2 cm brick lattice stamped over a timber panel, which is
       * what mat_closeup_wood showed once its vertical smear was gone. A rotation plus
       * a non-integer aspect means the micro copy shares no axis and no period with the
       * base, so it reads as grain instead of as a grid. Tangent normals are
       * counter-rotated, exactly as in hexFetchNormal. */
      float dt = uDetailP.x;
      float dca = cos(uDetail2.y), dsa = sin(uDetail2.y);
      mat2 dR = mat2(dca, -dsa * uDetail2.z, dsa, dca * uDetail2.z);
      vec2 duv = dR * (uv0 * dt) + vec2(0.371, 0.517);
      /* THIS TAP HAS TO BE ANISO-BOUNDED TOO — it is the same mistake that was found
       * and fixed in surfOctave and never fixed here. The detail layer re-tiles the
       * material's own normal map at up to 26x, so its footprint carries the SAME
       * anisotropy ratio as the base — in the hundreds at grazing incidence — while the
       * hardware stops at 16. Under-filtering along the view axis, drawn out, is a
       * horizontal streak, and this layer is a NORMAL map, so the streak arrives as a
       * shading artefact rather than a blur: "the left third of mat_closeup_concrete
       * dissolves into horizontal streaks", and the same failure on the level_detail
       * kerb side face. */
      vec2 ddx = dR * dx * dt, ddy = dR * dy * dt;
      surfAniso(ddx, ddy);
      vec4 dn = textureGrad(normalMap, duv, ddx, ddy);
      vec3 d3 = dn.xyz * 2.0 - 1.0;
      d3.xy = transpose(dR) * d3.xy;
      vec3 dts = normalize(d3 + vec3(0.0, 0.0, 1e-4));
      gNTS = blendNormalUDN(gNTS, dts, uDetailP.y);
      gORM.g = clamp(gORM.g + (dn.a - uDetailP.w) * uDetailP.z, 0.02, 1.0);
      gALB.rgb *= 1.0 + (dn.a - uDetailP.w) * uDetail2.x;
    }
    #endif

    #ifdef SURF_OCTAVE
      surfOctave(uv0, dx, dy, gALB, gORM);
    #endif
    gWNRM = vec3(0.0, 0.0, 1.0);
  #endif

  /* ---- façade: three vertical zones, sill streaks, per-building jitter ----
   *
   * Every exterior wall in the build was one flat colour with real punched
   * windows and nothing else — no plinth, no string course, no weathering, and
   * every building the same value. All three are read from world position, so
   * they cost no extra sampler, no extra draw call and no change to the level.
   *
   *   uFacadeP = ( plinth top m, storey height m, sill height in storey m, streak )
   *   uFacadeJ = ( value jitter, hue jitter, roughness jitter, 1/patch metres )
   */
  #ifdef SURF_FACADE
  {
    float wy = vSurfWPos.y;
    vec2  wxz = vSurfWPos.xz;
    float facing = 1.0 - abs(normalize(vSurfWNrm).y);   // walls only, not soffits

    /* -- per-building patch: a coarse, irregularly bounded cell of world space.
          Real streets are painted a building at a time and weather a building at
          a time; one value across a whole block is the giveaway. The boundary is
          warped by fbm so it reads as a render patch, not a UV seam. */
    vec2 cellP = wxz * uFacadeJ.w;
    cellP += vec2(sFbm(wxz * 0.09), sFbm(wxz * 0.09 + 31.7)) * 0.7;
    vec2 cid = floor(cellP);
    float jv = sHash21(cid + 3.17) - 0.5;
    float jh = sHash21(cid * 1.7 + 11.93) - 0.5;
    float jr = sHash21(cid * 2.3 + 27.41) - 0.5;
    gALB.rgb *= 1.0 + jv * uFacadeJ.x;
    gALB.rgb *= mix(vec3(1.0), vec3(1.0 + jh * 0.9, 1.0 + jh * 0.15, 1.0 - jh * 0.8), uFacadeJ.y);
    gORM.g = clamp(gORM.g + jr * uFacadeJ.z, 0.05, 1.0);

    /* -- zone 1: plinth. Splash-back, rising damp and kicked dirt live in the
          bottom metre of every street wall and nowhere else. */
    float wob = (sFbm(vec2(wxz.x + wxz.y, wy * 0.6) * 1.3) - 0.5) * 0.26;
    float plinth = 1.0 - smoothstep(uFacadeP.x * 0.55, uFacadeP.x + wob, wy);
    plinth *= facing;
    gALB.rgb *= mix(vec3(1.0), vec3(0.62, 0.60, 0.57), plinth * 0.9);
    gORM.g = clamp(gORM.g + plinth * 0.16, 0.05, 1.0);
    gORM.r = clamp(gORM.r * (1.0 - plinth * 0.22), 0.0, 1.0);

    /* -- zone 3: string course / cornice band at every storey line. Catches sun
          on its top face and holds a dirt line under it. */
    float sy = (wy - uFacadeP.z) / max(uFacadeP.y, 0.1);   // 0 at each sill
    float band = fract(sy + 0.735);
    float cor = smoothstep(0.02, 0.0, abs(band - 0.5) - 0.018) * facing;
    gALB.rgb *= 1.0 - cor * 0.30;
    gORM.g = clamp(gORM.g + cor * 0.12, 0.05, 1.0);

    /* -- weathering streaks running DOWN from every sill. t is zero exactly at a
          sill line and grows downward, so the run always starts under the
          opening and dies out about a metre below it. */
    float t = fract(-sy);
    float run = smoothstep(0.005, 0.05, t) * (1.0 - smoothstep(0.10, 0.34, t));
    float colN = sFbm(vec2((wxz.x - wxz.y) * 2.9, wy * 0.05) + cid * 0.7);
    float fine = sFbm(vec2((wxz.x - wxz.y) * 11.0, wy * 0.18));
    float streak = run * smoothstep(0.46, 0.78, colN * 0.75 + fine * 0.25) * facing;
    gALB.rgb *= 1.0 - streak * uFacadeP.w;
    gALB.rgb = mix(gALB.rgb, gALB.rgb * vec3(0.92, 0.95, 1.0), streak * 0.5);
    gORM.g = clamp(gORM.g + streak * 0.14, 0.05, 1.0);
    gORM.r = clamp(gORM.r * (1.0 - streak * 0.30), 0.0, 1.0);
  }
  #endif

  #ifdef SURF_MACRO
  {
    vec2 mp = vSurfWPos.xz * uMacroP.x + vSurfWPos.y * uMacroP.x * 0.35;
    float m1 = sFbm(mp);
    float m2 = sFbm(mp * 3.7 + 17.0);
    float v = (m1 - 0.5) * 1.6 + (m2 - 0.5) * 0.7;
    gALB.rgb *= 1.0 + v * uMacroP.y;
    gALB.rgb = mix(gALB.rgb, gALB.rgb * vec3(1.0 + v * 0.5, 1.0, 1.0 - v * 0.5), uMacroP.w);
    gORM.g = clamp(gORM.g + v * uMacroP.z, 0.02, 1.0);

    /* A SECOND, DECORRELATED ROUGHNESS FIELD.
     *
     * The line above modulates albedo and roughness from the SAME noise, which ties
     * gloss to value: everywhere the surface is lighter it is also glossier. That is
     * not how a surface works and it is why the macro layer never produced a readable
     * roughness change however hard it was driven — a lighter, glossier patch and a
     * darker, matter patch are both just "more of the same material", so the eye reads
     * one variable, not two.
     *
     * Real spatial roughness comes from CONTACT and from FILM: a traffic path polished
     * into a floor, a greasy band where hands land, a matte zone where overspray or
     * dust has settled. None of them is correlated with the albedo mottle. So this is
     * its own field, at its own scale, signed both ways — dust one side, polish the
     * other — and it is deliberately LOW frequency, because a roughness variation the
     * mip chain can average is a roughness variation that does not exist.
     *
     *   uMacroP2 = ( field scale 1/m, amplitude, polish albedo coupling, - )
     */
    if (uMacroP2.y > 0.001) {
      vec3 rp = vSurfWPos * uMacroP2.x;
      float r1 = sFbm(rp.xz + rp.y * 0.61 + 41.3);
      float r2 = sFbm(rp.zx * 2.31 + rp.y * 0.29 + 77.9);
      float rv = (r1 - 0.5) * 1.5 + (r2 - 0.5) * 0.6;
      gORM.g = clamp(gORM.g + rv * uMacroP2.y, 0.02, 1.0);
      // A polished zone darkens a little (it is the same pigment with the scattering
      // taken out of it); a dusty zone lightens. Small, and coupled to the roughness
      // rather than driving it.
      gALB.rgb *= 1.0 + rv * uMacroP2.z;
    }
  }
  #endif

  /* ---- curvature-driven edge wear --------------------------------------
   *
   * FINDINGS F20 named this as the #2 gap against shipped Call of Duty, and it also
   * named the counter-example: "the only exception in the entire dev set is the rusted
   * barrel rim, which does have real edge wear — and it is visibly the best-looking
   * object in eight frames". Nothing else in the build has been knocked.
   *
   * Two independent estimates of convexity, because neither one alone covers the
   * geometry this build actually contains, and BOTH come out of data that is already
   * in the shader — no fifth sampler (FINDINGS F6 caps us at ~4).
   *
   * 1. GEOMETRIC, in inverse metres. dFdx(vSurfWPos) is metres per pixel and
   *    dFdx(N) is the normal's change per pixel, so
   *        k = (dN/dx . dP/dx + dN/dy . dP/dy) / (|dP/dx|^2 + |dP/dy|^2)
   *    is a real curvature with a real unit, positive where the surface bulges away
   *    from itself. Being in 1/m it is RESOLUTION AND DISTANCE INDEPENDENT: a 1 mm
   *    arris reads 1000/m whether it is 30 cm or 6 m away, so the threshold can be
   *    authored as "wear anything sharper than a 8 mm radius" and mean it.
   *
   *    It is faded out with distance anyway (uEdgeP2.w). Past a few metres a real
   *    arris is under a pixel wide, and a sub-pixel bright line is not detail, it is
   *    aliasing — the specular-shimmer failure this file already fights elsewhere.
   *
   * 2. RELIEF, from the material's own height field (normalMap.a) gated by its baked
   *    cavity AO. Proud AND open is the definition of a spot that gets rubbed: the
   *    top of an aggregate stone, a brick arris, a plank edge, the crown of a rust
   *    scab. This one is mip-filtered, so it survives to any distance and it is what
   *    carries the effect on flat walls and floors, where there is no geometric edge
   *    at all.
   *
   * The two are combined with max(), not added: a spot is worn if EITHER the geometry
   * or the surface puts it in the firing line, and adding them double-counts an arris
   * whose texture also happens to be proud there.
   *
   * Wear is then broken up by a world-space field, because a perfectly even bright
   * line along every edge is its own tell — real wear is patchy, heavier where a hand
   * or a boot actually lands. The albedo mix is QUADRATIC while roughness and
   * metalness ramp linearly, for the reason the weapon library learned the hard way:
   * a bare-metal albedo is an order of magnitude above a coating's, so a linear mix
   * at even 10 % wear washes the whole surface toward the worn colour.
   *
   *   uEdgeP  = ( strength, curvature onset 1/m, worn roughness, worn metalness )
   *   uEdgeP2 = ( relief weight, height datum, break-up scale 1/m, geo fade metres )
   */
  #ifdef SURF_EDGEWEAR
  {
    vec3 gN  = normalize(vSurfWNrm);
    vec3 dpx = dFdx(vSurfWPos), dpy = dFdy(vSurfWPos);
    vec3 dnx = dFdx(gN),        dny = dFdy(gN);
    float den = dot(dpx, dpx) + dot(dpy, dpy) + 1e-12;
    float kGeo = (dot(dnx, dpx) + dot(dny, dpy)) / den;       // 1/m, + = convex
    float geoFade = 1.0 - smoothstep(uEdgeP2.w, uEdgeP2.w * 2.0, viewDist);
    float eGeo = smoothstep(uEdgeP.y, uEdgeP.y * 4.0, max(kGeo, 0.0)) * geoFade;

    /* The resolve pass maps the height field's own MEAN to 0.88 and clips at 1.0
     * (passes.glsl.js), so "above the mean" saturates and cannot discriminate on its
     * own — the first version of this block lit up every flat surface in the build.
     * What identifies a rubbed shoulder is three things at once: above the datum,
     * ON A SLOPE (a plateau is never the first thing to get knocked), and OPEN. The
     * slope is taken from the BASE tangent normal captured in surfFetch, before the
     * detail layer adds its micro-relief — otherwise every grain everywhere qualifies
     * and the wear reads as a bloom over the whole surface. */
    float eRel = smoothstep(uEdgeP2.y, min(uEdgeP2.y + 0.055, 0.999), gHGT)
               * smoothstep(0.08, 0.36, gSLP)
               * smoothstep(0.50, 0.92, gORM.r) * uEdgeP2.x;

    float edge = max(eGeo, eRel);

    // patchiness — two decorrelated world-space octaves, biased to keep more than
    // half of every edge worn so the effect reads as wear and not as dirt
    vec3 bp = vSurfWPos * uEdgeP2.z;
    float bk = sFbm(bp.xz + bp.y * 0.83) * 0.6 + sFbm(bp.zy * 1.73 + bp.x * 0.61 + 5.7) * 0.4;
    edge *= smoothstep(0.24, 0.66, bk) * 0.75 + 0.25;

    float w = clamp(edge * uEdgeP.x, 0.0, 1.0);
    gALB.rgb = mix(gALB.rgb, uEdgeCol, w * w);
    gORM.g = clamp(mix(gORM.g, uEdgeP.z, w), 0.02, 1.0);
    gORM.b = clamp(mix(gORM.b, uEdgeP.w, w), 0.0, 1.0);
    gORM.r = clamp(gORM.r + w * 0.10, 0.0, 1.0);   // an arris sees more of the sky
  }
  #endif

  /* ---- wet / polished zones -------------------------------------------
   *
   * FINDINGS F13, and the reviewer's own restatement of it: the build has no
   * specular anywhere because "no window returns a sun reflection, no surface is wet,
   * no metal is bare". 60 of 69 scene materials sit at roughness >= 0.9, and at that
   * roughness there is no mirror direction to point at anything.
   *
   * A wet patch is the cheapest honest source of a real highlight on a horizontal
   * surface: water fills the cavities, so it is BOTH a roughness floor and an albedo
   * darkening, and it is masked by the surface's own baked cavity map so it collects
   * where a puddle would actually sit. Horizontal faces only — running water on a wall
   * is a different phenomenon and reads wrong.
   *
   *   uWetP = ( world scale, strength, coverage threshold, wet roughness floor )
   */
  #ifdef SURF_WET
  {
    vec2 wp = vSurfWPos.xz * uWetP.x;
    float m = sFbm(wp) * 0.62 + sFbm(wp * 2.87 + 9.31) * 0.38;
    float cav = 1.0 - gORM.r;                          // baked AO: 0 open, 1 deep
    float up = clamp(normalize(vSurfWNrm).y * 2.2 - 0.35, 0.0, 1.0);
    float wet = smoothstep(uWetP.z, uWetP.z + 0.20, m + cav * 0.30) * uWetP.y * up;
    gALB.rgb *= mix(1.0, 0.46, wet);                   // water darkens what it soaks
    gORM.g = mix(gORM.g, uWetP.w, wet);                // and flattens the micro-relief
    gORM.r = mix(gORM.r, 1.0, wet * 0.6);              // a film fills the cavity AO
  }
  #endif

  diffuseColor *= gALB;
}
`;

/** material -> { uniforms, flags, opts } for the injected shading */
const SURF_STATE = new WeakMap();

/** Scale a colour to luminance 1 so it can only shift hue, never brightness. */
function lumaNormalised(c) {
  const y = 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
  if (y > 1e-4) c.multiplyScalar(1 / y);
  return c;
}

/** @param {THREE.Material} mat */
export function getSurfaceShading(mat) { return SURF_STATE.get(mat) || null; }

/**
 * @param {THREE.Material} mat
 * @param {object} o    shading options
 * @param {object} res  { detailNormal }
 */
export function applySurfaceShading(mat, o = {}, res = {}) {
  const flags = [];
  if (o.triplanar) flags.push('SURF_TRIPLANAR');
  if (o.pom) flags.push('SURF_POM');
  if (o.detail !== false) flags.push('SURF_DETAIL');
  if (o.hex) flags.push('SURF_HEX');
  if (o.macro) flags.push('SURF_MACRO');
  if (o.octave) flags.push('SURF_OCTAVE');
  if (o.facade) flags.push('SURF_FACADE');
  if (o.specAA !== false) flags.push('SURF_SPECAA');
  if (o.translucent) flags.push('SURF_TRANSLUCENT');
  if (o.uvJitter) flags.push('SURF_UVJITTER');
  if (o.wet) flags.push('SURF_WET');
  if (o.edgeWear) flags.push('SURF_EDGEWEAR');
  if (o.sss) flags.push('SURF_SKIN');

  const key = 'surf:' + flags.join('|');
  const defs = flags.map((f) => `#define ${f}`).join('\n') + '\n';

  const uniforms = {
    uDetailP: { value: new THREE.Vector4(o.detailTile ?? 14, o.detailStrength ?? 0.7, o.detailRough ?? 0.16, o.detailDatum ?? 0.82) },
    uMacroP: { value: new THREE.Vector4(o.macroScale ?? 0.06, o.macroAlbedo ?? 0.16, o.macroRough ?? 0.05, o.macroHue ?? 0.15) },
    // roughField is authored as the METRES one zone spans, so "a 2 m polished patch"
    // is written as 2, not as 0.5.
    uMacroP2: {
      value: new THREE.Vector4(
        1 / Math.max(o.roughFieldMetres ?? 3.0, 1e-3),
        o.roughField ?? 0,
        o.roughFieldAlbedo ?? 0,
        0,
      ),
    },
    uPomP: { value: new THREE.Vector4(o.pom ?? 0.02, o.pomLayers ?? 32, o.pomFade ?? 6, (o.pomFade ?? 6) * 2.2) },
    uTileP: { value: new THREE.Vector4(o.triplanarScale ?? 0.5, o.triplanarSharp ?? 4, o.hexNear ?? 4.5, o.hexFar ?? 45) },
    uUvXform: { value: new THREE.Vector4(o.repeat?.[0] ?? 1, o.repeat?.[1] ?? 1, o.offset?.[0] ?? 0, o.offset?.[1] ?? 0) },
    uAoIntensity: { value: o.aoIntensity ?? 1.0 },
    uTransP: { value: new THREE.Vector4(o.translucent ?? 0, o.transPower ?? 3.0, o.transWrap ?? 0.55, o.transTintMix ?? 0.5) },
    uTransTint: { value: new THREE.Color(o.transTint ?? 0x8fbf4a) },
    // 0.2831 is deliberately not a simple fraction: the second octave must share
    // no common period with the first or the two beat against each other.
    uOctP: { value: new THREE.Vector4(o.octaveScale ?? 0.2831, o.octaveAlbedo ?? 0.30, o.octaveRough ?? 0.10, o.octaveRot ?? 1.05) },
    // x albedo modulation from the detail height, y detail-layer rotation (rad),
    // z detail-layer aspect — deliberately irrational-ish so the micro copy of the
    // material's own map never lands back in register with the base
    uDetail2: { value: new THREE.Vector4(o.detailAlbedo ?? 0.35, o.detailRotate ?? 0.9599, o.detailAspect ?? 1.37, 0) },
    uFacadeP: { value: new THREE.Vector4(o.plinthTop ?? 1.05, o.storeyHeight ?? 3.4, o.sillHeight ?? 0.9, o.streak ?? 0.34) },
    uFacadeJ: { value: new THREE.Vector4(o.jitterValue ?? 0.26, o.jitterHue ?? 0.16, o.jitterRough ?? 0.16, 1 / (o.patchMetres ?? 13.0)) },
    uMiscP: {
      value: new THREE.Vector4(
        1 / (o.anisoRatio ?? 16),      // hardware's own limit; below it the GPU already resolves
        o.anisoMaxWiden ?? 3.0,        // never smear more than 3x across the view
        o.hexRestore ?? 1.18,          // clamp on the variance-restore gain (see hexFetch)
        o.metalGain ?? 1.0,            // scales the ORM metal channel
      ),
    },
    uJitterP: { value: new THREE.Vector4(1 / (o.uvJitterMetres ?? 2.0), o.uvJitter ?? 0, 0, 0) },
    // per-material IBL weight — see the aomap_fragment patch for why this cannot live
    // on material.envMapIntensity
    uSpecP: { value: new THREE.Vector4(o.ibl ?? 1.0, 0, 0, 0) },
    uWetP: {
      value: new THREE.Vector4(
        1 / (o.wetMetres ?? 6.0),      // puddle field scale
        o.wet ?? 0,                    // strength
        o.wetThreshold ?? 0.60,        // coverage: higher = fewer, smaller patches
        o.wetRough ?? 0.09,            // roughness inside a puddle. THIS is the specular.
      ),
    },
    /* Curvature edge wear. `edgeCurve` is authored as the RADIUS in metres that counts
     * as a knocked edge, not as a curvature — 8 mm is a kerb arris, 0.5 mm is a
     * machined chamfer — because a radius is a thing you can hold in your hand and
     * 1/0.008 is not. FINDINGS F15: an acceptance number has to be traceable to a
     * measurement of the thing being imitated, and so does a tuning parameter. */
    uEdgeP: {
      value: new THREE.Vector4(
        o.edgeWear ?? 0,
        1 / Math.max(o.edgeCurve ?? 0.010, 1e-4),
        o.edgeRough ?? 0.30,
        o.edgeMetal ?? 0.0,
      ),
    },
    uEdgeP2: {
      value: new THREE.Vector4(
        o.edgeRelief ?? 0.85,
        o.edgeDatum ?? 0.935,
        1 / Math.max(o.edgeBreakMetres ?? 0.45, 1e-3),
        o.edgeGeoFade ?? 7.0,
      ),
    },
    uEdgeCol: { value: new THREE.Color(o.edgeColor ?? 0xb9b2a6) },
    uSssP: {
      value: new THREE.Vector4(
        o.sss ?? 0,
        o.sssWrap?.[0] ?? 1.30,        // red diffuses furthest — haemoglobin passes it
        o.sssWrap?.[1] ?? 0.30,
        o.sssWrap?.[2] ?? 0.14,
      ),
    },
    uSssP2: { value: new THREE.Vector4(o.sssShadowFloor ?? 0.45, o.sssCurvature ?? 0.65, o.sssHotDesat ?? 0, 0) },
    uSssTint: { value: new THREE.Color(o.sssTint ?? 0xff6a44) },
    // Normalised to unit luminance: this is a HUE decision (sky-lit skin is cooler than
    // sunlit skin), not a brightness decision. Left un-normalised it quietly took 17 %
    // off every ambient-lit face, which is somebody else's exposure budget.
    uSssAmb: { value: lumaNormalised(new THREE.Color(o.sssAmbient ?? 0xe8f2ff)) },
  };

  // Side table, NOT userData: Material.copy() deep-clones userData through JSON, and a
  // THREE.Texture inside it makes three try to serialise the texture on every clone().
  SURF_STATE.set(mat, { uniforms, flags, opts: o });

  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);

    /* ---- vertex ---- */
    shader.vertexShader = defs +
      'varying vec3 vSurfWPos;\nvarying vec3 vSurfWNrm;\n' +
      shader.vertexShader
        .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
  vSurfWPos = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;`)
        .replace('#include <defaultnormal_vertex>', `#include <defaultnormal_vertex>
  vSurfWNrm = normalize( mat3( modelMatrix ) * objectNormal );`);

    /* ---- fragment ----
     * The injected helpers reference map/normalMap/vViewPosition, so they have to
     * land AFTER the pars includes — i.e. immediately before main(). */
    let f = defs + shader.fragmentShader.replace(
      'void main() {',
      PARS + SAMPLE_FN + (o.pom ? POM_FN : '') + '\nvoid main() {');

    f = f.replace('#include <map_fragment>', MAP_FRAGMENT);

    f = f.replace('#include <roughnessmap_fragment>',
      'float roughnessFactor = roughness * gORM.g;');

    /* Metalness comes from the ORM map's blue channel, NOT from material.metalness.
     *
     * The old form was metalness * gORM.b with every material carrying
     * metalness = 1 as a "multiplier". That reads as physically incoherent from
     * JavaScript — prop:palm_tree:wood_plank genuinely reports metalness 1 — and,
     * worse, it BECOMES incoherent the moment anything calls .clone() outside
     * make(): THREE.Material.copy() does not copy onBeforeCompile, so the clone
     * loses this injection, metalnessMap was never bound (F6: samplers are the scarce
     * resource) and the raw 1.0 goes straight through. That is a wooden trunk rendering
     * as bare chrome. Now the scalar is authored per material as the honest fallback for
     * exactly that case, and the map wins whenever the injection is live. */
    f = f.replace('#include <metalnessmap_fragment>',
      'float metalnessFactor = clamp( gORM.b * uMiscP.w, 0.0, 1.0 );');

    f = f.replace('#include <normal_fragment_maps>', `
  #ifdef SURF_TRIPLANAR
    normal = normalize( mat3( viewMatrix ) * gWNRM );
  #else
    vec3 mapN = gNTS;
    mapN.xy *= normalScale;
    normal = normalize( gTBN * mapN );
  #endif
`);

    f = f.replace('#include <aomap_fragment>', `
  {
    float ambientOcclusion = ( gORM.r - 1.0 ) * uAoIntensity + 1.0;
    reflectedLight.indirectDiffuse *= ambientOcclusion;
    #if defined( USE_SHEEN )
      sheenSpecularIndirect *= ambientOcclusion;
    #endif
    #if defined( USE_CLEARCOAT )
      clearcoatSpecularIndirect *= ambientOcclusion;
    #endif
    #if defined( USE_ENVMAP ) && defined( STANDARD )
      float dotNVao = saturate( dot( geometryNormal, geometryViewDir ) );
      reflectedLight.indirectSpecular *= computeSpecularOcclusion( dotNVao, ambientOcclusion, material.roughness );
    #endif
    /* PER-MATERIAL IBL WEIGHT, because three.js will not give us one.
     *
     * WebGLRenderer.setProgram overwrites material.envMapIntensity with
     * scene.environmentIntensity for ANY material whose own envMap is null — and ours
     * are all null on purpose, because scene.environment is the lighting team's real
     * sky PMREM and binding a second copy per material would spend texture image units
     * we do not have (F6). So every authored IBL weight in this library was void: a
     * matte handguard reflected the sky exactly as hard as a bare barrel. Scaling the
     * indirect specular here is the same knob, applied where nothing can steal it, and
     * it is still multiplied by whatever absolute scale the lighting team owns. */
    reflectedLight.indirectSpecular *= uSpecP.x;
    #ifdef SURF_SKIN
      reflectedLight.indirectDiffuse *= uSssAmb;
    #endif
  }
`);

    f = f.replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
  #ifdef SURF_SKIN
  #if NUM_DIR_LIGHTS > 0
  {
    /* PER-CHANNEL DIFFUSION WRAP — the thing that makes skin look like skin.
     *
     * The critique measured 6.0 degrees of hue travel across the whole \`skin\` sphere
     * and 4.2 degrees across cheek / nose / jaw, against the 25-40 real skin needs, and
     * the previous pass tried to buy it with albedo zoning alone. It cannot be bought
     * that way. On a head, most of skin's hue travel is not painted into the map at
     * all — it is a SHADING effect: light entering the dermis scatters millimetres
     * before it leaves, haemoglobin absorbs green and blue on the way, so the light
     * that emerges a little past the geometric terminator is red and the light that
     * bounces straight off the lit side is not. That is why a photographed face reddens
     * into the terminator and why its SATURATION goes UP as its luminance goes DOWN —
     * an inversion no albedo texture can produce, because an albedo texture does not
     * know where the light is.
     *
     * So: wrap the diffuse term by a DIFFERENT amount per channel (red furthest), take
     * the difference against the ordinary clamped N.L that three has already added, and
     * add that difference tinted by the transmission colour. At N.L = 1 the difference
     * is exactly zero, so the lit side is untouched and this cannot wash the frame out
     * the way an additive sheen/clearcoat stack did.
     *
     * The sun term is gated by the lighting team's own cascade, with a FLOOR rather
     * than a hard multiply: the light arriving just past the terminator entered the
     * flesh on the lit side and came out in shadow, so it is genuinely not shadowed by
     * the shadow map, and gating it to zero would delete the entire effect exactly
     * where it belongs.
     *
     *   uSssP  = ( strength, wrapR, wrapG, wrapB )
     *   uSssP2 = ( shadow floor, curvature gain, highlight desaturation, - )
     */
    vec3 wrapK = max( uSssP.yzw, vec3( 1e-3 ) );
    float sssG = uSssP.x;
    float hotNdl = 0.0;   // how hard the strongest key faces this fragment
    #ifdef AAA_LIGHTING
      vec3 skinSunView = normalize( ( viewMatrix * vec4( aaaSunDirection, 0.0 ) ).xyz );
      float skinSunShadow = aaaSunShadow( vAaaWorldPos,
        transformNormalByInverseViewMatrix( geometryNormal, viewMatrix ), vViewPosition.z );
    #endif
    /* Curvature widens the effect: a nose tip or an ear is thin and small-radius, so
     * more of what enters comes back out. dFdx of the shading normal against the depth
     * derivative is the standard cheap estimate and costs nothing extra here. */
    vec3 cndx = dFdx( normal ), cndy = dFdy( normal );
    float curv = sqrt( dot( cndx, cndx ) + dot( cndy, cndy ) );
    float curvK = 1.0 + clamp( curv * 8.0, 0.0, 1.0 ) * uSssP2.y;
    for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {
      vec3 L = directionalLights[ i ].direction;
      float ndl = dot( normal, L );
      vec3 wrapped = clamp( ( vec3( ndl ) + wrapK ) / ( 1.0 + wrapK ), 0.0, 1.0 );
      vec3 delta = max( wrapped - vec3( saturate( ndl ) ), vec3( 0.0 ) );
      /* A BAND, not a wash into the dark side.
       *
       * The raw wrap difference peaks at N.L = 0 and stays high all the way to
       * N.L = -1, where the ordinary diffuse is zero — so the ratio of "scattered red"
       * to "everything else" runs away and a face in cast shadow prints as a
       * hard-edged sunburn, which is exactly what the first tuning of this produced.
       * Real subsurface light reaching a point past the terminator entered the flesh a
       * few millimetres away on the LIT side, so it dies within roughly a third of a
       * radian of the terminator and does not exist deep in shadow. */
      delta *= smoothstep( -0.45, -0.05, ndl );
      float g = 1.0;
      #ifdef AAA_LIGHTING
        if ( dot( normalize( L ), skinSunView ) > 0.999 ) g = mix( uSssP2.x, 1.0, skinSunShadow );
      #endif
      reflectedLight.directDiffuse += directionalLights[ i ].color * delta * uSssTint
        * diffuseColor.rgb * ( sssG * curvK * RECIPROCAL_PI * g );
      hotNdl = max( hotNdl, saturate( ndl ) * g );
    }

    /* SATURATION MUST FALL INTO THE HIGHLIGHT.
     *
     * The wrap above is the half of this that everyone remembers: light that emerges
     * past the terminator has taken a long path through the dermis and comes back red,
     * so chroma rises as luminance falls. The half that was missing is the other end.
     * Where the key strikes near-normal, most of what returns is a short single-scatter
     * from the epidermis and the specular lobe, neither of which has been through much
     * haemoglobin — so the brightest skin in a photograph is the LEAST saturated skin in
     * it, and a face whose highlight is its most saturated region reads as sunburn. The
     * iteration-3 build measured exactly that (nose 0.568 against cheek 0.487).
     *
     * Implemented as a mix toward the term's own Rec.709 luma, so it is chroma-only:
     * it cannot add energy, cannot brighten a pixel, and therefore cannot be the cause
     * of a clip. Gated on N.L rather than on the frame's luminance, because a
     * frame-derived gate would be a different value in every shot. */
    if ( uSssP2.z > 0.001 ) {
      float hot = smoothstep( 0.30, 0.92, hotNdl ) * uSssP2.z;
      vec3 dd = reflectedLight.directDiffuse;
      reflectedLight.directDiffuse = mix( dd, vec3( dot( dd, vec3( 0.2126, 0.7152, 0.0722 ) ) ), hot );
    }
  }
  #endif
  #endif
  #ifdef SURF_TRANSLUCENT
  #if NUM_DIR_LIGHTS > 0
  {
    /* Cheap two-sided subsurface: light that punched through the leaf/cloth keeps going
     * roughly forward, plus a wrapped term so the shadowed side never goes black.
     *
     * Two things were wrong here and together they made every grass tuft in the alley
     * read as a fluorescent yellow-green emitter:
     *
     * 1. NO RECIPROCAL_PI. directionalLights[i].color is radiance premultiplied by
     *    intensity, and every other diffuse term in the shader goes through
     *    BRDF_Lambert, i.e. is divided by PI. Adding this one straight to
     *    directDiffuse put it 3.14x above the scale the rest of the frame is authored
     *    on, on top of a strength of 1.15 — at a 17.5-unit sun that is ~10 scene units
     *    off a surface whose sunlit diffuse is ~0.5.
     * 2. NO SHADOW. The sun term here is the raw light colour, so a leaf standing in
     *    full shade transmitted full noon sun. Self-illumination, literally.
     *
     * The sun's shadow is recovered through the lighting team's own cascade function,
     * matched by direction exactly the way inject.js matches it, so a showcase stage
     * lit by its own key is unaffected. */
    vec3 trans = vec3( 0.0 );
    #ifdef AAA_LIGHTING
      vec3 transSunView = normalize( ( viewMatrix * vec4( aaaSunDirection, 0.0 ) ).xyz );
      float transSunShadow = aaaSunShadow( vAaaWorldPos,
        transformNormalByInverseViewMatrix( geometryNormal, viewMatrix ), vViewPosition.z );
    #endif
    for ( int i = 0; i < NUM_DIR_LIGHTS; i ++ ) {
      vec3 L = directionalLights[ i ].direction;
      float fwd = pow( saturate( dot( geometryViewDir, -L ) ), uTransP.y );
      float wrap = saturate( ( dot( normal, L ) + uTransP.z ) / ( 1.0 + uTransP.z ) ) - saturate( dot( normal, L ) );
      float k = fwd * 0.70 + wrap * 0.50;
      #ifdef AAA_LIGHTING
        if ( dot( normalize( L ), transSunView ) > 0.999 ) k *= transSunShadow;
      #endif
      trans += directionalLights[ i ].color * k;
    }
    vec3 transCol = mix( diffuseColor.rgb, uTransTint, uTransP.w );
    reflectedLight.directDiffuse += trans * transCol * ( uTransP.x * RECIPROCAL_PI );
  }
  #endif
  #endif
`);

    f = f.replace('#include <lights_physical_fragment>', `#include <lights_physical_fragment>
  #ifdef SURF_SPECAA
  {
    vec3 ndx = dFdx( normal ), ndy = dFdy( normal );
    float nvar = 0.5 * ( dot( ndx, ndx ) + dot( ndy, ndy ) );
    /* Cap 0.22 -> 0.10. A 0.22 kernel is 0.47 of roughness added in one quad, which is
     * enough to turn ANY sharp shading-normal transition into a visible dark line — the
     * hex blend band, a POM silhouette, a bevel. It is a variance clamp, not a licence
     * to repaint the surface; 0.10 still kills the shimmer on distant detail. */
    float kernel = min( nvar * 2.0, 0.10 );
    material.roughness = clamp( sqrt( material.roughness * material.roughness + kernel ), 0.02, 1.0 );
  }
  #endif
`);

    shader.fragmentShader = f;
  };

  mat.customProgramCacheKey = () => key;

  /* ---- make the injection survive .clone() -------------------------------
   *
   * THREE.Material.copy() copies a fixed list of named properties and
   * onBeforeCompile is not on it, so a clone silently loses ALL of this: the
   * detail layer, POM, the façade zones, the macro variation, the translucency —
   * and, because metalnessMap is deliberately not bound (F6), the metalness too.
   * Materials.make() knows to re-apply it; nothing else does, and
   * Env.js :: assignProbes clones every probe-lit material directly. That is one
   * clone() between a wooden palm trunk and bare chrome.
   *
   * Overriding clone on the instance fixes it for every caller, including the
   * ones this module cannot see. Lighting's registerMaterial wraps
   * onBeforeCompile and chains to whatever was there before, so re-applying
   * here — before the clone is registered — composes correctly.
   */
  mat.clone = function surfClone() {
    const c = new this.constructor().copy(this);
    return applySurfaceShading(c, o, res);
  };

  mat.needsUpdate = true;
  return mat;
}
